import { useRef, useState, type ChangeEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { DRINK_KIND_LABELS, type Bottle, type BottleDetails } from '../../shared/api';
import { BottleForm } from '../components/BottleForm';
import { BottleLevelPanel } from '../components/BottleLevelPanel';
import { useResource } from '../hooks/useResource';
import { api, ApiError, errorMessage } from '../lib/api';
import { formatSize, pictureOf } from '../lib/bottles';
import { formatDate } from '../lib/format';
import { shrinkPhoto } from '../lib/photo';

export function BottlePage() {
  const { id = '' } = useParams();
  const bottleId = Number(id);
  const navigate = useNavigate();
  const { data: bottle, error, loading, reload, setData } = useResource(`bottle:${id}`, (signal) => api.getBottle(bottleId, signal));
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState<'refreshing' | 'deleting' | 'photo' | null>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionNote, setActionNote] = useState<string | null>(null);

  async function saveEdit(details: BottleDetails) {
    setData(await api.updateBottle(bottleId, details));
    setEditing(false);
  }

  /** Fills in details the barcode lookup knows and this bottle doesn't; never overwrites what's been typed. */
  async function refresh(current: Bottle) {
    if (!current.barcode) return;
    setBusy('refreshing');
    setActionError(null);
    setActionNote(null);
    try {
      const { metadata } = await api.lookupDrink(current.barcode);
      const changes: Partial<BottleDetails> = {};
      if (metadata) {
        for (const field of ['brand', 'sizeMl', 'abv', 'imageUrl'] as const) {
          if (current[field] === null && metadata[field] !== null) Object.assign(changes, { [field]: metadata[field] });
        }
      }
      if (Object.keys(changes).length === 0) {
        setActionNote(metadata ? 'Nothing new to add.' : 'Open Food Facts has no record of this barcode.');
      } else {
        setData(await api.updateBottle(bottleId, changes));
      }
    } catch (err) {
      setActionError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  async function choosePhoto(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setBusy('photo');
    setActionError(null);
    try {
      setData(await api.setBottlePhoto(bottleId, await shrinkPhoto(file)));
    } catch (err) {
      setActionError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  async function removePhoto() {
    setBusy('photo');
    setActionError(null);
    try {
      setData(await api.deleteBottlePhoto(bottleId));
    } catch (err) {
      setActionError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  async function remove(current: Bottle) {
    if (!window.confirm(`Remove "${current.name}" from your bar?`)) return;
    setBusy('deleting');
    setActionError(null);
    try {
      await api.deleteBottle(bottleId);
      navigate('/bar', { replace: true });
    } catch (err) {
      setActionError(errorMessage(err));
      setBusy(null);
    }
  }

  const backLink = (
    <Link className="back-link" to="/bar">
      ← Bar
    </Link>
  );

  if (!bottle) {
    if (loading) return <p className="page-status">Loading…</p>;
    const notFound = error instanceof ApiError && (error.status === 404 || error.status === 400);
    return (
      <section className="page-status" role="alert">
        {backLink}
        <p className={notFound ? undefined : 'error-text'}>{notFound ? 'That bottle is not in your bar.' : errorMessage(error)}</p>
        {!notFound && (
          <button type="button" className="btn btn--primary" onClick={reload}>
            Try again
          </button>
        )}
      </section>
    );
  }

  const details: Array<[label: string, value: string | null]> = [
    ['Brand', bottle.brand],
    ['Kind', DRINK_KIND_LABELS[bottle.kind]],
    ['Vintage', bottle.variant],
    ['Size', formatSize(bottle.sizeMl)],
    ['ABV', bottle.abv === null ? null : `${bottle.abv}%`],
    ['One pour', bottle.serveMl === null ? 'A whole one' : `${bottle.serveMl} ml`],
    ['Barcode', bottle.barcode],
    ['Added', formatDate(bottle.addedAt)],
    ['Last checked', formatDate(bottle.levelCheckedAt)],
  ];

  return (
    <article className="book-page bottle-page">
      {backLink}
      <div className="bottle-page__header">
        <button
          type="button"
          className="bottle-page__photo-button"
          onClick={() => photoInputRef.current?.click()}
          disabled={busy !== null}
          title={pictureOf(bottle) ? 'Change photo' : 'Add a photo'}
        >
          {pictureOf(bottle) ? (
            <img className="bottle-page__image" src={pictureOf(bottle)!} alt="" loading="eager" />
          ) : (
            <span className="bottle-page__image bottle-page__image--empty">{busy === 'photo' ? '…' : '📷'}</span>
          )}
        </button>
        <div>
          <h1>{bottle.name}</h1>
          {bottle.variant && <p className="subtitle">{bottle.variant}</p>}
          {bottle.brand && <p className="authors">{bottle.brand}</p>}
          <p className="bottle-page__photo-actions">
            <button type="button" className="link-button" onClick={() => photoInputRef.current?.click()} disabled={busy !== null}>
              {busy === 'photo' ? 'Saving photo…' : pictureOf(bottle) ? 'Change photo' : 'Add a photo'}
            </button>
            {bottle.photoUrl && (
              <>
                {' · '}
                <button type="button" className="link-button" onClick={() => void removePhoto()} disabled={busy !== null}>
                  Remove photo
                </button>
              </>
            )}
          </p>
          {/* capture="environment" opens the camera straight away on phones; desktops get a file picker. */}
          <input ref={photoInputRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => void choosePhoto(e)} />
        </div>
      </div>

      <BottleLevelPanel bottle={bottle} onChange={setData} />

      {editing ? (
        <section className="card" aria-label="Edit bottle">
          <BottleForm initial={bottle} submitLabel="Save changes" onSubmit={saveEdit} onCancel={() => setEditing(false)} />
        </section>
      ) : (
        <dl className="details">
          {details
            .filter((entry): entry is [string, string] => entry[1] !== null)
            .map(([label, value]) => (
              <div key={label} className="details__row">
                <dt>{label}</dt>
                <dd className={label === 'Barcode' ? 'mono' : undefined}>{value}</dd>
              </div>
            ))}
        </dl>
      )}

      {actionError && (
        <p className="error-text" role="alert">
          {actionError}
        </p>
      )}
      {actionNote && (
        <p className="muted" role="status">
          {actionNote}
        </p>
      )}
      {!editing && (
        <div className="actions">
          <button type="button" className="btn btn--secondary" onClick={() => setEditing(true)} disabled={busy !== null}>
            Edit details
          </button>
          {bottle.barcode && (
            <button type="button" className="btn btn--secondary" onClick={() => void refresh(bottle)} disabled={busy !== null}>
              {busy === 'refreshing' ? 'Looking up…' : 'Fill in from barcode'}
            </button>
          )}
          <button type="button" className="btn btn--danger" onClick={() => void remove(bottle)} disabled={busy !== null}>
            {busy === 'deleting' ? 'Removing…' : 'Remove'}
          </button>
        </div>
      )}
    </article>
  );
}
